import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Grid2, TextField, Typography } from '@mui/material';
import { useMuiController, useMuiForm } from '@zenput/util-common';
import { useSnackbar } from 'notistack';
import { makeStyles } from 'tss-react/mui';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import PageLayout from '../../shared/PageLayout';
import { SimpleGallery } from '../../shared/SimpleGallery/SimpleGallery';
import { DeleteFollowUpActionModal } from './DeleteFollowUpActionModal';
import { EditSavedFollowUpAction } from './EditSavedFollowUpAction';
import {
    FollowUpActionType,
    FollowUpActionReplySchema,
    ReplylFollowUpActionType,
    ZenputFileType
} from './FollowUpActions.types';
import { FollowUpActionsGallery } from './FollowUpActionsGallery';

const useStyles = makeStyles()((theme) => {
    const { spacing, typography } = theme;

    return {
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: `${spacing(3)} ${spacing(2)}`,
            textTransform: 'none',
            backgroundColor: 'white'
        },
        assignContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            padding: `0 ${spacing(2)}`,
            display: 'flex',
            flexDirection: 'column'
        },
        submissionContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            padding: `${spacing(2)} ${spacing(2)}`,
            display: 'flex',
            flexDirection: 'column'
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        addFUAButton: {
            textTransform: 'none',
            paddingLeft: spacing(2),
            alignSelf: 'flex-start'
        },
        boxImageContainer: {
            height: spacing(14)
        },
        boxReplyImageContainer: {
            position: 'relative',
            width: 80,
            height: 80,
            borderRadius: 1,
            overflow: 'hidden',
            marginTop: `${spacing(2)} !important`
        },
        imgPreview: {
            width: '100%',
            height: '100%',
            objectFit: 'cover'
        },
        textFieldInput: {
            marginBottom: spacing(2),
            width: '100%',
            '& .MuiInputBase-input': {
                border: 'none',
                background: 'none'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                fontSize: typography.body2.fontSize,
                color: 'black !important'
            }
        }
    };
});

export enum FUAReplies {
    BASIC = 'basic',
    PHOTO = 'photo'
}

interface ShowFollowUpActionProps {
    completeFollowUpActionOnSuccess: (message: string) => void;
    deleteFollowUpAction: (id: number, onError) => void;
    getStorageFullUrl: (value: string) => string;
    initialFollowUpAction: FollowUpActionType;
    navigateToSubmission: (id: string) => void;
    onGoBack: () => void;
    updateFollowUpAction: (follow: FollowUpActionType, onComplete: (response) => void, onError: () => void) => void;
    uploadFollowUpFiles: (files: ZenputFileType[]) => string[];
}

export const ShowFollowUpAction = (props: ShowFollowUpActionProps) => {
    const {
        completeFollowUpActionOnSuccess,
        deleteFollowUpAction,
        getStorageFullUrl,
        initialFollowUpAction,
        navigateToSubmission,
        onGoBack,
        updateFollowUpAction,
        uploadFollowUpFiles
    } = props;

    const [followUpAction, setFollowUpAction] = useState(initialFollowUpAction);

    const {
        data: dataPayload,
        date_completed,
        has_delete_permission,
        has_update_permission,
        location,
        reply_type,
        source_id,
        title
    } = initialFollowUpAction;
    const { description, images, reply } = dataPayload;

    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [galleryOpen, setGalleryOpen] = useState(false);
    const [galleryOpenReply, setGalleryOpenReply] = useState(false);

    const [openEditScreen, setOpenEditScreen] = useState(false);
    const [openDeleteScreen, setOpenDeleteScreen] = useState(false);

    const [s3keys, setS3Keys] = useState<string[]>([]);

    const handleAddS3Keys = (keys) => {
        setS3Keys((prev) => prev.concat(keys));
    };

    const handleDeleteS3keys = (indexToRemove) => {
        setS3Keys((prevKeys) => prevKeys.filter((_, index) => index !== indexToRemove));
    };

    const {
        control,
        handleSubmit,
        formState: { errors }
    } = useMuiForm<ReplylFollowUpActionType>({
        resolver: zodResolver(FollowUpActionReplySchema),
        defaultValues: { status: 'completed', images: [] }
    });

    const { field: imagesField } = useMuiController({ control, name: 'images' });
    const { field: noteField } = useMuiController({ control, name: 'note' });

    useEffect(() => {
        if (JSON.stringify(errors) != '{}') {
            enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
        }
    }, [errors]);

    const galleryItems =
        images.length > 0 &&
        images.map((img, idx) => ({
            id: `${idx}-${img}`,
            src: getStorageFullUrl(img)
        }));

    const galleryItemsReply =
        reply.images &&
        reply.images.length > 0 &&
        reply.images.map((img, idx) => ({
            id: `${idx}-${img}`,
            src: getStorageFullUrl(img)
        }));

    const toolbarMenuItems = [
        {
            title: _t('mobile_common_button_edit'),
            onClick: () => setOpenEditScreen(true),
            visible: has_update_permission
        },
        {
            title: _t('mobile_drafts_button_delete'),
            onClick: () => setOpenDeleteScreen(true),
            visible: has_delete_permission
        }
    ];

    const openGallery = () => setGalleryOpen(true);
    const closeGallery = () => setGalleryOpen(false);
    const openGalleryReply = () => setGalleryOpenReply(true);
    const closeGalleryReply = () => setGalleryOpenReply(false);

    const updateFollowUpActionSuccess = async (updatedFollowUpAction) => {
        updateFollowUpAction(
            updatedFollowUpAction,
            (response) => {
                setFollowUpAction(response);
                enqueueSnackbar(_t('mobile_mobileform_follow_up_actions_update_notification'), { variant: 'success' });
            },
            () => {
                enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
            }
        );
        setOpenEditScreen(false);
    };

    const deleteFollowUpActionSuccess = async () => {
        deleteFollowUpAction(followUpAction.id, () => {
            enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
        });
        setOpenDeleteScreen(false);
    };

    const onSubmit = handleSubmit(async (values) => {
        if (reply_type === FUAReplies.PHOTO && !values.images.length) {
            enqueueSnackbar(_t('mobile_mobileform_follow_up_actions_required_photo_error'), { variant: 'error' });
            return;
        }

        const updatedFollowUpAction = { ...followUpAction };
        updatedFollowUpAction.data = {
            description,
            images,
            reply: {
                images: [''],
                note: '',
                status: 'completed'
            }
        };

        if (values.images) {
            updatedFollowUpAction.data.reply = {
                images: s3keys,
                note: values.note ?? '',
                status: 'completed'
            };
        }

        updateFollowUpAction(
            updatedFollowUpAction,
            () => {
                completeFollowUpActionOnSuccess('mobile_mobileform_follow_up_actions_completed_notification');
            },
            () => {
                enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
            }
        );
    });

    return (
        <PageLayout>
            {{
                appBar: (
                    <AppBar
                        toolbarMenuItems={
                            !date_completed && (has_delete_permission || has_update_permission) && toolbarMenuItems
                        }
                        title={_t('mobile_mobileform_follow_up_actions')}
                        onGoBack={onGoBack}
                    />
                ),
                content: (
                    <>
                        <div className={classes.assignContainer}>
                            {galleryOpen && (
                                <SimpleGallery items={galleryItems} onClose={closeGallery} startIndex={0} />
                            )}
                            {galleryOpenReply && (
                                <SimpleGallery items={galleryItemsReply} onClose={closeGalleryReply} startIndex={0} />
                            )}
                            <div className="task_details">
                                <div className="card_body">
                                    <h3>{title}</h3>

                                    <div className="detail_items">
                                        {location && (
                                            <div className="detail_item task_account">
                                                <a href="#" className="view_task_account">
                                                    <i className="df-location-filled" />
                                                    {location.name}
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    {description && (
                                        <div className="task_description">
                                            <p className="task_description_text">{description}</p>
                                        </div>
                                    )}

                                    {images.length > 0 && (
                                        <Grid2 container direction="row" spacing={2} sx={{ marginBottom: 2 }}>
                                            {images.map((image, index) => (
                                                <Grid2 key={index} size={{ xs: 4, md: 3 }}>
                                                    <Box className={classes.boxImageContainer}>
                                                        <img
                                                            alt={`preview ${index}`}
                                                            className={classes.imgPreview}
                                                            onClick={openGallery}
                                                            src={getStorageFullUrl(image)}
                                                        />
                                                    </Box>
                                                </Grid2>
                                            ))}
                                        </Grid2>
                                    )}
                                </div>
                            </div>

                            {reply_type && reply_type == 'photo' && !date_completed && (
                                <div>
                                    <FollowUpActionsGallery
                                        isReply
                                        handleAddS3Keys={handleAddS3Keys}
                                        handleDeleteS3keys={handleDeleteS3keys}
                                        uploadFollowUpFiles={uploadFollowUpFiles}
                                        {...imagesField}
                                    />
                                    <TextField
                                        className={classes.textFieldInput}
                                        label={_t('mobile_field_textarea_placeholder_note')}
                                        variant="outlined"
                                        multiline
                                        {...noteField}
                                    />
                                </div>
                            )}

                            {reply.images && reply.images.length > 0 && (
                                <Grid2
                                    container
                                    direction="row"
                                    spacing={2}
                                    sx={{ borderTop: '.5px solid lightgray', marginBottom: 2 }}
                                >
                                    {reply.images.map((image, index) => (
                                        <Grid2 sx={{ marginTop: 2 }} key={index} size={{ xs: 4, md: 3 }}>
                                            <Box className={classes.boxImageContainer}>
                                                <img
                                                    alt={`preview ${index}`}
                                                    className={classes.imgPreview}
                                                    onClick={openGalleryReply}
                                                    src={getStorageFullUrl(image)}
                                                />
                                            </Box>
                                        </Grid2>
                                    ))}
                                </Grid2>
                            )}
                        </div>

                        <div className={classes.submissionContainer}>
                            {source_id && (
                                <div>
                                    <Typography sx={{ color: 'black', fontWeight: 'medium' }} variant="subtitle1">
                                        {_t('mobile_mobileform_follow_up_actions_submission')}
                                    </Typography>
                                    <Button
                                        sx={{ marginTop: 2, padding: 1, textTransform: 'none' }}
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => navigateToSubmission(source_id)}
                                    >
                                        {_t('mobile_mobileform_follow_up_actions_navigate_to_submission')}
                                    </Button>
                                </div>
                            )}
                        </div>

                        <EditSavedFollowUpAction
                            followUpAction={followUpAction}
                            getStorageFullUrl={getStorageFullUrl}
                            onGoBack={() => setOpenEditScreen(false)}
                            open={openEditScreen}
                            updateFollowUpActionSuccess={updateFollowUpActionSuccess}
                            uploadFollowUpFiles={uploadFollowUpFiles}
                        />

                        <DeleteFollowUpActionModal
                            description={_t('mobile_mobileform_follow_up_actions_forms_delete_modal_subtitle')}
                            handleCloseConfirmation={() => setOpenDeleteScreen(false)}
                            no={_t('mobile_action_sheet_cancel')}
                            onSubmit={deleteFollowUpActionSuccess}
                            showConfirmation={openDeleteScreen}
                            title={_t('mobile_mobileform_follow_up_actions_forms_delete_modal_title')}
                            yes={_t('mobile_drafts_button_delete')}
                        />
                    </>
                ),

                bottomBar: (
                    <BottomBar>
                        {!date_completed && (
                            <div className={classes.bottomBar}>
                                <Button
                                    className={classes.bottomButton}
                                    variant="contained"
                                    color="primary"
                                    onClick={onSubmit}
                                >
                                    {_t('mobile_mobileform_follow_up_actions_complete_follow_up_action')}
                                </Button>
                            </div>
                        )}
                    </BottomBar>
                )
            }}
        </PageLayout>
    );
};
